'use client';

import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, AlertCircle } from 'lucide-react';
import Link from 'next/link';
import { useAuthMutations } from '@/lib/queries/auth.mutations';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import * as z from 'zod';
import { useRouter } from 'next/navigation';

const formSchema = z.object({
  email: z.string().email({
    message: 'Please enter a valid email address.',
  }),
  password: z.string().min(6, {
    message: 'Password must be at least 6 characters.',
  }),
  rememberMe: z.boolean().default(false),
});

export function LoginForm() {
  const { login, isLoginPending } = useAuthMutations();
  const router = useRouter();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      email: '',
      password: '',
      rememberMe: false,
    },
  });

  async function onSubmit(values: z.infer<typeof formSchema>) {
    try {
      const result = await login({
        email: values.email,
        password: values.password,
      });

      console.log('Login result:', result);

      if ('error' in result) {
        // Handle API validation errors (e.g., { email: ["Invalid format"] })
        if (result.error && typeof result.error === 'object' && !result.error.general) {
          Object.entries(result.error).forEach(([field, messages]) => {
            if (Array.isArray(messages) && messages.length > 0) {
              form.setError(field as keyof z.infer<typeof formSchema>, {
                type: 'manual',
                message: messages[0],
              });
            }
          });
        } else {
          // Handle general errors
          form.setError('root', {
            type: 'manual',
            message:
              Array.isArray(result.error?.general) && result.error.general.length > 0
                ? result.error.general[0]
                : 'An unexpected error occurred',
          });
        }
      } else {
        console.log('Login successful, redirecting to dashboard');
        // Login successful, redirect to dashboard
        console.log('Attempting to redirect to /dashboard...');
        router.push('/dashboard');
        console.log("router.push('/dashboard') called.");
      }
    } catch (error) {
      // Catch unexpected errors during the mutation call itself
      console.error('Login submission error:', error);
      form.setError('root', {
        type: 'manual',
        message: 'An unexpected network or server error occurred.',
      });
    }
  }

  return (
    <div className="w-full max-w-[420px] mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-semibold tracking-tight">Sign in to your account</h1>
        <p className="text-sm text-muted-foreground mt-2">Enter your email below to access your account</p>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {form.formState.errors.root && (
            <Alert variant="destructive" className="text-sm">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{form.formState.errors.root.message}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-4">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input {...field} type="email" placeholder="you@example.com" className="h-11" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center justify-between">
                    <FormLabel>Password</FormLabel>
                  </div>
                  <FormControl>
                    <Input {...field} type="password" className="h-11" />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex justify-between items-center">
              <FormField
                control={form.control}
                name="rememberMe"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                    <FormControl>
                      <Checkbox checked={field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                    <FormLabel className="text-sm font-normal leading-none">Remember me</FormLabel>
                  </FormItem>
                )}
              />
              <Link href="/forgot-password" className="text-xs text-primary hover:text-primary/90 hover:underline">
                Forgot password?
              </Link>
            </div>
          </div>

          <Button type="submit" disabled={isLoginPending} className="w-full h-11">
            {isLoginPending ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Signing in...
              </>
            ) : (
              'Sign in'
            )}
          </Button>
        </form>
      </Form>
    </div>
  );
}
